import {createFileRoute} from "@tanstack/react-router";
import {z as zod} from "zod";
import type {AuthenticationStatus} from "~/routes/_withAuthenticationStatus/-types";
import {jsonZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {getHeimdallUserWithCaching, getUserProfileDetailsWithCaching, getPlatformAuthorizationDetailsWithCaching} from "~/utilities/queries";
import {AuthenticationPlatformAccessOperation} from "@vesta/vos-types/modules/platformLevel/authentication/domain/AuthenticationPlatformAccessOperation";
import {PlatformModuleForAuthorization} from "@vesta/vos-types/modules/platformLevel/authorization/domain/PlatformAuthorization";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {HEIMDALL_LOCAL_STORAGE_KEY} from "~/utilities/constants";
import {getImpersonationSession, setImpersonationSessionTargetUserName} from "~/utilities/impersonationSession";

export const Route = createFileRoute("/_withAuthenticationStatus")({
    beforeLoad: async ({context}) => {
        const heimdallString = localStorage.getItem(HEIMDALL_LOCAL_STORAGE_KEY);
        const accessTokenResult = jsonZodValidator
            .pipe(
                zod.object({
                    session: zod.object({
                        accessToken: zod.string(),
                        refreshToken: zod.string().optional(),
                    }),
                }),
            )
            .safeParse(heimdallString);
        let primaryAccessToken: string | null;
        if (accessTokenResult.success !== true) {
            primaryAccessToken = null;
        } else {
            primaryAccessToken = accessTokenResult.data.session.accessToken;
        }

        if (primaryAccessToken === null) {
            const authenticationStatus: AuthenticationStatus = {
                isAuthenticated: false,
                accessToken: null,
                heimdallUser: null,
            };
            return {authenticationStatus: authenticationStatus, primaryCanImpersonate: false};
        }

        const impersonationSession = getImpersonationSession();
        const accessToken = impersonationSession?.accessToken ?? primaryAccessToken;

        const heimdallUserResult = await getHeimdallUserWithCaching({
            accessToken: accessToken,
            queryClient: context.queryClient,
            intellsysBaseUrl: context.intellsysBaseUrl,
        });
        if (heimdallUserResult.success !== true) {
            throw heimdallUserResult.error;
        }
        const heimdallUser = heimdallUserResult.data;

        if (heimdallUser === null) {
            const authenticationStatus: AuthenticationStatus = {
                isAuthenticated: false,
                accessToken: null,
                heimdallUser: null,
            };
            return {authenticationStatus: authenticationStatus, primaryCanImpersonate: false};
        }

        if (impersonationSession !== null && impersonationSession.targetUserName === "the selected user") {
            const impersonatedUserProfileResult = await getUserProfileDetailsWithCaching({
                accessToken: accessToken,
                queryClient: context.queryClient,
                intellsysBaseUrl: context.intellsysBaseUrl,
            });
            const impersonatedUserProfile =
                impersonatedUserProfileResult.success === true ? impersonatedUserProfileResult.data : null;
            const targetUserName =
                impersonatedUserProfile?.name ??
                impersonatedUserProfile?.email ??
                impersonatedUserProfile?.phoneNumber ??
                heimdallUser.id;
            setImpersonationSessionTargetUserName({targetUserName: targetUserName});
        }

        // Read the original user's permission without changing the impersonated user's access.
        let primaryCanImpersonate = false;
        if (impersonationSession !== null) {
            try {
                const primaryAuthorization = await getPlatformAuthorizationDetailsWithCaching({
                    accessToken: primaryAccessToken,
                    queryClient: context.queryClient,
                    intellsysBaseUrl: context.intellsysBaseUrl,
                });
                const primaryAccessMatrix: Record<string, Record<string, boolean>> | null =
                    primaryAuthorization.success === true ? primaryAuthorization.data : null;
                primaryCanImpersonate = primaryAccessMatrix?.[PlatformModuleForAuthorization.authentication]?.[AuthenticationPlatformAccessOperation.quickImpersonation] === true;
            } catch {
                // A failed optional UI permission check must not interrupt the active session.
                primaryCanImpersonate = false;
            }
        }

        const authenticationStatus: AuthenticationStatus = {
            isAuthenticated: true,
            accessToken: accessToken,
            heimdallUser: heimdallUser,
        };
        return {
            authenticationStatus: authenticationStatus,
            primaryCanImpersonate: primaryCanImpersonate,
        };
    },
    loader: async () => {},
    staleTime: Infinity,
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
